import logo from "assets/logo_verify.svg";
import { Box, Typography, Grid, Link as MuiLink, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  useGetUserByVerificationCodeQuery,
  useSetPasswordMutation,
} from "../../redux/api/authApi";
import { Field } from "components/Shared";
import { showErrorToast, showSuccessToast } from "components/Toast/Toast";
import FullScreenLoader from "components/FullScreenLoader";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { Dialog, DialogContent, DialogContentText, Checkbox, FormControlLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";


const StyledDialogTitle = styled(Box)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  position: 'relative',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

const LinkItem = styled(Link)`
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

interface DialogTitleProps {
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle: React.FC<DialogTitleProps> = ({ children, onClose }) => {
  return (
    <StyledDialogTitle>
      {children}
      {onClose && (
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      )}
    </StyledDialogTitle>
  );
};

interface TermsDialogProps {
  open: boolean;
  handleClose: () => void;
}

const TermsDialog: React.FC<TermsDialogProps> = ({ open, handleClose }) => {
  const { formatMessage } = useIntl();
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle onClose={handleClose} ><Typography sx={{ fontSize: "19px", color: "#000000" }}>{formatMessage({ id: "new.password.service.title" })}</Typography></DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography variant="body1" sx={{ fontSize: "0.9rem", marginBottom: "0.8rem" }}>
            []{formatMessage({ id: "new.password.service.1" })}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "0.9rem", marginBottom: "0.8rem" }}>
            {formatMessage({ id: "new.password.service.2" })}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "0.9rem", marginBottom: "0.8rem" }}>
            {formatMessage({ id: "new.password.service.3" })}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "0.9rem", marginBottom: "0.8rem" }}>
          {formatMessage({ id: "new.password.service.4" })} <MuiLink href="mailto:email.provisional@viacoreit.com">email.provisional@viacoreit.com</MuiLink>
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "0.9rem", marginBottom: "0.8rem" }}>
          {formatMessage({ id: "new.password.service.5" })} (<MuiLink href="www.google.es">www.link.provisional.es</MuiLink>).
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "0.9rem", marginBottom: "0.8rem" }}>
          {formatMessage({ id: "new.password.service.6" })}
          </Typography>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

const resetPasswordSchema = object({
  password: string()
    .min(1, "La contraseña es obligatoria")
    .min(8, "La contraseña debe tener al menos 8 caracteres"),
  passwordConfirm: string().min(1, "Introduce la nueva contraseña"),
}).refine((data) => data.password === data.passwordConfirm, {
  message: "Las contraseñas no coinciden",
  path: ["passwordConfirm"],
});

export type ResetPasswordInput = TypeOf<typeof resetPasswordSchema>;

export const NewPasswordPage = () => {
  const { formatMessage } = useIntl();
  const { resetToken } = useParams<{ resetToken: string }>();
  const [acceptedTerms, setAcceptedTerms] = useState(false);  // Estado para controlar si se ha aceptado las condiciones
  const [open, setOpen] = useState(false);

  const methods = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
  });
  // Funciones para manejar el modal
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { data: user, isLoading: isGetUserLoading } =
    useGetUserByVerificationCodeQuery(String(resetToken));

  // 👇 API Login Mutation
  const [setPassword, { isLoading, isError, error, isSuccess }] =
    useSetPasswordMutation();


  const { handleSubmit } = methods;

  useEffect(() => {
    if (isSuccess) {
      showSuccessToast("Usuario registrado correctamente");
    }
  }, [isLoading, isSuccess]);

  useEffect(() => {
    if (isError) {
      if (Array.isArray((error as any).data?.error)) {
        (error as any).data.error.forEach((el: any) =>
          showErrorToast(el.message)
        );
      } else {
        showErrorToast((error as any).data.message);
      }
    }
  }, [error, isError]);


  const onSubmitHandler: SubmitHandler<ResetPasswordInput> = (values) => {
    const response = values.password;
    const data = response;
    if (!acceptedTerms) {
      return;
    }

    const formData: FormData = new FormData();
    formData.append("data", data);
    setPassword({ userId: String(user?.id), password: values.password });
  };

  if (isGetUserLoading) {
    <FullScreenLoader />;
  }
  if (isSuccess) {
    return (<Grid
      container
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        height: "100vh",
        overflowY: "auto",
        width: "100vw",
        padding: 0,
      }}
    >
      <Grid
        item
        md={7}
        sx={{
          display: { xs: "none", sm: "block" },
        }}
      >
        <Box
          sx={{
            height: "100%",
            backgroundColor: "lightblue", // Solo para visualización
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Contenido de la sección del 60% aquí */}
          <Typography variant="h4">{formatMessage({ id: "new.password.title" })}</Typography>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sm={5}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          minHeight: "100%",
          backgroundImage: `url()`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: "1.60rem",
        }}
      >
        <Box sx={{
          maxWidth: "27rem"
        }}
          display="flex"
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          <Box
            sx={{
              margin: {
                xs: "2rem 1.625rem", // Margen superior para teléfonos móviles
                sm: "2rem", // Margen superior para tablets
                md: "3rem", // Margen superior para desktop
                // Puedes agregar más breakpoints si es necesario
              },
              height: {
                xs: "5.25rem", // Altura para teléfonos móviles
                sm: "80px", // Altura para tablets
                md: "100px", // Altura para desktop
                // Puedes agregar más breakpoints si es necesario
              },
            }}
          >
            <img src={logo} style={{ height: '100%', width: 'auto' }} alt="Logo" />
          </Box>
          <Box
            sx={{
              marginBottom: {
                xs: "2rem", // Margen superior para teléfonos móviles
                sm: "2rem", // Margen superior para tablets
                md: "3rem", // Margen superior para desktop
                // Puedes agregar más breakpoints si es necesario
              },
              height: {
                xs: "5.625rem", // Altura para teléfonos móviles
                sm: "80px", // Altura para tablets
                md: "100px", // Altura para desktop
                // Puedes agregar más breakpoints si es necesario
              },
            }}
          >
          </Box>
          <Typography
            textAlign="left"
            component="h1"
            sx={{
              width: "100%",
              fontWeight: 600,
              fontSize: { xs: "1.5rem", md: "2rem", color: "#8F114C" },
              lineHeight: "1.813rem",
            }}
          >
            {formatMessage({ id: "new.password.title" })}
          </Typography>
          <Typography
            textAlign="left"
            component="h2"
            sx={{
              width: "100%",
              fontWeight: 500,
              fontSize: { xs: "1rem", color: "#090A0A" },
              marginBottom: "1.2rem",
            }}
          >
            {formatMessage({ id: "new.password.info" })}
          </Typography>
          <LinkItem to={"/login"}>{formatMessage({ id: "new.password.login" })}</LinkItem>
        </Box>
      </Grid>
    </Grid>
    );

  }
  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        height: "100vh",
        overflowY: "auto",
        width: "100vw",
        padding: 0,
      }}
    >
      <Grid
        item
        md={7}
        sx={{
          display: { xs: "none", sm: "block" },
        }}
      >
        <Box
          sx={{
            height: "100%",
            backgroundColor: "lightblue", // Solo para visualización
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Contenido de la sección del 60% aquí */}
          <Typography variant="h4">{formatMessage({ id: "new.password.activate" })}</Typography>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sm={5}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          minHeight: "100%",
          backgroundImage: `url()`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: "1.60rem",
        }}
      >
        <Box sx={{
          maxWidth: "27rem"
        }}
          display="flex"
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          <Box
            sx={{
              margin: {
                xs: "2rem 1.625rem", // Margen superior para teléfonos móviles
                sm: "2rem", // Margen superior para tablets
                md: "3rem", // Margen superior para desktop
                // Puedes agregar más breakpoints si es necesario
              },
              height: {
                xs: "5.25rem", // Altura para teléfonos móviles
                sm: "80px", // Altura para tablets
                md: "100px", // Altura para desktop
                // Puedes agregar más breakpoints si es necesario
              },
            }}
          >
            <img src={logo} style={{ height: '100%', width: 'auto' }} alt="Logo" />
          </Box>
          <Box
            sx={{
              marginBottom: {
                xs: "2rem", // Margen superior para teléfonos móviles
                sm: "2rem", // Margen superior para tablets
                md: "3rem", // Margen superior para desktop
                // Puedes agregar más breakpoints si es necesario
              },
              height: {
                xs: "5.625rem", // Altura para teléfonos móviles
                sm: "80px", // Altura para tablets
                md: "100px", // Altura para desktop
                // Puedes agregar más breakpoints si es necesario
              },
            }}
          >
          </Box>
          <Typography
            textAlign="left"
            component="h1"
            sx={{
              width: "100%",
              fontWeight: 600,
              fontSize: { xs: "1.5rem", md: "2rem", color: "#8F114C" },
              lineHeight: "1.813rem",
            }}
          >
            Activar cuenta
          </Typography>
          <Typography
            textAlign="left"
            component="h2"
            sx={{
              width: "100%",
              fontWeight: 500,
              fontSize: { xs: "1rem", color: "#090A0A" },
              marginBottom: "1.2rem",
            }}
          >
            {formatMessage({ id: "new.password.activate" })}
          </Typography>
          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmitHandler)}
              noValidate
              autoComplete="off"
              maxWidth="27rem"
              width="100%"
            >
              <Field.Password
                autoComplete="new-password"
                name="password"
                label="Nueva contraseña"
              />
              <Field.Password
                autoComplete="new-password"
                name="passwordConfirm"
                label="Repite la contraseña"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(event: any) => setAcceptedTerms(event.target.checked)}
                    name="acceptTerms"
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.81rem" }}>
                    {formatMessage({ id: "new.password.accept" })}{' '}
                    <MuiLink component="button" variant="body2" sx={{ fontSize: "0.81rem" }} onClick={handleOpen}>
                    {formatMessage({ id: "new.password.conditions" })}
                    </MuiLink>
                  </Typography>
                }
              />
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <LoadingButton
                  variant="contained"
                  disabled={!acceptedTerms}
                  sx={{
                    mt: 1,
                    backgroundColor: "#8F114C",
                    width: "100%",
                    height: "4.188rem",
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    borderRadius: "1.188rem",
                    textTransform: "none",
                  }}
                  disableElevation
                  type="submit"
                  loading={isLoading}
                >
                  {formatMessage({ id: "new.password.save" })}
                </LoadingButton>
              </Box>
            </Box>
          </FormProvider>
        </Box>
        {/* Modal para las condiciones del servicio */}
        <TermsDialog open={open} handleClose={handleClose} />
      </Grid>
    </Grid>
  );
};